import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { EOrgType } from '@unfoldrtech/portal-mic'
import { EDatasetKey, EReportColumnHeader, EReportingTimeWindow } from '@unfoldrtech/portal-mic/dist/src/models/core'

import {
  AdGroupContext,
  AppContext,
  CampaignContext,
} from '../../../../models/contexts'

import {
  IDOOHReportLocationsTable,
  TReportExportType,
  TReportingFilters,
} from '../../../../models'

import {
  selectDOOHLocationTableReportingFilters,
  setAdGroupIds,
  setCampaignIds,
  setIsReportingV3,
} from '../../../../store/reportingFilters'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { ReportingDOOHLocationChartWrapper } from './ReportingDOOHLocationChartWrapper'
import { ReportingDOOHLocationTableWrapper } from './ReportingDOOHLocationTableWrapper'

import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import { TDatasetWhitelistKey } from '../../models'
import { generateCorrelationId, getTimezone } from '../../../../utils/helpers'
import { coreAPI } from '../../../../services/coreAPI'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'

/**
 * One report template per entity
 * Ex: ReportCategoriesTemplate, ReportCampaignsTemplate, etc ...
 */
export const ReportingDOOHLocationTemplate = () => {
  const pageTitle = 'reporting.header.locations'

  const dispatch = useDispatch()
  const intl = useIntl()

  const [appContext] = useContext(AppContext)
  const campaign = useContext(CampaignContext)
  const adGroup = useContext(AdGroupContext)
  const filters = useSelector(selectDOOHLocationTableReportingFilters)

  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()
  const orgType = appContext.userOrg!.type

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<IDOOHReportLocationsTable>()
  const [reportingFiltersIsSetup, setReportingFiltersIsSetup] = useState(false)

  const onTableDataChange = (locationsTableData: IDOOHReportLocationsTable) => {
    setTableData(locationsTableData)
  }

  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) ||
      EDatasetKey.LocationDooh,
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res =
        orgType === EOrgType.Advertiser
          ? await coreAPI.v2AdvertiserReporting().getDOOHAdvertiserLocationsReportTable(
              advertiserId,
              generateCorrelationId(),
              retailerId,
              campaign?.id,
              adGroup?.id,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as unknown as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status
            )
          : await coreAPI.v2RetailerReporting().getDOOHRetailerLocationsReportTable(
              retailerId,
              generateCorrelationId(),
              advertiserId,
              campaign?.id,
              adGroup?.id,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status
            )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    dispatch(
      setCampaignIds({
        campaignIds: campaign?.id,
      })
    )
    dispatch(
      setAdGroupIds({
        adGroupIds: adGroup?.id,
      })
    )
    setReportingFiltersIsSetup(true)
  }, [campaign.id, adGroup.id])

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  return (
    <>
      {reportingFiltersIsSetup && (
        <>
          <ReportPageTemplateLayout
            title={pageTitle}
            isExportDisabled={isExporting || !tableData?.datasetKey}
            chartWrapper={<ReportingDOOHLocationChartWrapper />}
            tableWrapper={
              <ReportingDOOHLocationTableWrapper
                onTableDataChange={onTableDataChange}
              />
            }
            onExportData={onExportData}
          />
          <SuccessToast
            show={showExportCappedToast}
            onClose={() => setShowExportCappedToast(false)}
            translationKey="reporting.export.capped"
          />
          <ErrorToast
            show={showExportErrorToast}
            onClose={() => setShowExportErrorToast(false)}
            translationKey="reporting.export.failed"
          />
        </>
      )}
    </>
  )
}
